import { isSandboxEnvironment } from './environment';

const PROVIDER_KEYS = {
    stripe:    ['priceID', 'discountID'],
    paddle:    ['priceId', 'discountId'],
    solidgate: ['productPriceId', 'couponId'],
};

const LOG_PREFIX = '[currencyAvailability]';

function isNonEmpty(v) {
    return typeof v === 'string' && v.trim() !== '';
}

function planHasProvider(plan, currencyKey, provider) {
    const block = plan?.prices?.[currencyKey];
    if (!block) return false;
    // In sandbox a configured stripeSandbox block fulfills the stripe
    // provider; otherwise fall back to the production stripe IDs check.
    if (provider === 'stripe' && isSandboxEnvironment()) {
        if (PROVIDER_KEYS.stripe.every(k => isNonEmpty(block.stripeSandbox?.[k]))) return true;
    }
    // Likewise, a configured paddleSandbox block fulfills the paddle provider
    // in sandbox; otherwise fall back to the production paddle IDs check.
    if (provider === 'paddle' && isSandboxEnvironment()) {
        if (PROVIDER_KEYS.paddle.every(k => isNonEmpty(block.paddleSandbox?.[k]))) return true;
    }
    return PROVIDER_KEYS[provider].every(k => isNonEmpty(block[provider]?.[k]));
}

function planLabel(plan) {
    return plan?.id ?? plan?.title ?? '?';
}

function collectPlans(board) {
    const out = [];
    const walk = (node) => {
        if (Array.isArray(node)) return node.forEach(walk);
        if (node && typeof node === 'object') {
            if (Array.isArray(node.plans)) out.push(...node.plans);
            Object.values(node).forEach(walk);
        }
    };
    walk(board);
    return out;
}

export function isCurrencyAvailable(currency) {
    if (!currency) {
        console.log(`${LOG_PREFIX} unavailable: currency is empty`);
        return false;
    }
    const board = typeof window !== 'undefined' ? window.__BOARD_DATA__ : null;
    if (!board) {
        console.log(`${LOG_PREFIX} "${currency}" unavailable: window.__BOARD_DATA__ is missing`);
        return false;
    }
    const key = String(currency).toLowerCase();
    const plans = collectPlans(board);
    if (plans.length === 0) {
        console.log(`${LOG_PREFIX} "${currency}" available: board has no plan elements (nothing to validate)`);
        return true;
    }

    // DEBUG: dump raw provider blocks per plan to trace empty stripe IDs in sandbox
    console.log(
        `${LOG_PREFIX} DEBUG currency="${key}" environment="${board?.system?.environment}" isSandbox=${isSandboxEnvironment()} plans=${plans.length}`
    );
    plans.forEach(p => {
        const block = p?.prices?.[key];
        console.log(
            `${LOG_PREFIX} DEBUG plan="${planLabel(p)}" priceKeys=[${Object.keys(p?.prices ?? {}).join(', ')}] block "${key}":`,
            block
                ? {
                    stripe: block.stripe,
                    stripeSandbox: block.stripeSandbox,
                    paddle: block.paddle,
                    paddleSandbox: block.paddleSandbox,
                    solidgate: block.solidgate,
                }
                : '(no block for this currency)'
        );
    });

    const matchingProvider = Object.keys(PROVIDER_KEYS).find(provider =>
        plans.every(p => planHasProvider(p, key, provider))
    );

    if (matchingProvider) {
        console.log(
            `${LOG_PREFIX} "${currency}" available: every plan (${plans.length}) has fully-configured "${matchingProvider}"`
        );
        return true;
    }

    const breakdown = Object.keys(PROVIDER_KEYS).map(provider => {
        const missing = plans
            .filter(p => !planHasProvider(p, key, provider))
            .map(planLabel);
        return missing.length === 0
            ? `  - ${provider}: ok for all plans`
            : `  - ${provider}: missing on plan(s) [${missing.join(', ')}]`;
    });

    console.log(
        `${LOG_PREFIX} "${currency}" unavailable: no single provider is fully configured across all ${plans.length} plan(s)\n${breakdown.join('\n')}`
    );
    return false;
}
